import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Rating } from 'primereact/rating';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';
import { Chip } from 'primereact/chip';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './BookingsWithReview.css';
import useAuth from "hooks/useAuth";

const BookingsWithReview = () => {
  // State for bookings list
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for review modal
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewData, setReviewData] = useState({
    userId: '',
    partnerShopId: '',
    bookingId: '',
    rating: 0,
    reviewTitle: '',
    reviewText: ''
  });
  
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Toast reference
  const toast = React.useRef(null);
   const auth = useAuth();
  // Get current user ID (from auth context/local storage)
  const currentUserId = auth.userId; // localStorage.getItem('userId') || 1;
  const API_URL = process.env.REACT_APP_API_PATH;

  // Fetch bookings for the user
  useEffect(() => {
    fetchUserBookings();
  }, []);

  const fetchUserBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}customer/user/${currentUserId}`);
      setBookings(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load bookings',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Open review modal with booking data
  const openReviewModal = (booking) => {
    setSelectedBooking(booking);
    setReviewData({
      userId: booking.userId || booking.userid,
      partnerShopId: booking.partnerShopId || booking.partnerid,
      bookingId: booking.id,
      rating: 0,
      reviewTitle: '',
      reviewText: ''
    });
    setShowModal(true);
    setSubmitStatus(null);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
    setReviewData({
      userId: '',
      partnerShopId: '',
      bookingId: '',
      rating: 0,
      reviewTitle: '',
      reviewText: ''
    });
  };

  // Handle form input changes
  const handleInputChange = (e, field) => {
    setReviewData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!reviewData.rating || reviewData.rating < 1) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select a rating',
        life: 3000
      });
      return false;
    }
    if (!reviewData.reviewTitle.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter a review title',
        life: 3000
      });
      return false;
    }
    if (!reviewData.reviewText.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter your review',
        life: 3000
      });
      return false;
    }
    if (reviewData.reviewText.length < 10) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Review must be at least 10 characters',
        life: 3000
      });
      return false;
    }
    return true;
  };

  // Submit review
  const handleSubmitReview = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API_URL}customer/reviews`, {
        PartnerShopId: parseInt(reviewData.partnerShopId),
        UserId: parseInt(reviewData.userId),
        BookingId: parseInt(reviewData.bookingId),
        Rating: reviewData.rating,
        ReviewTitle: reviewData.reviewTitle,
        ReviewText: reviewData.reviewText
      });
      
      setSubmitStatus('success');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Thank you! Your review has been submitted.',
        life: 3000
      });
      
      // Update the booking in the list to show reviewed status
      setBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.id === selectedBooking.id
            ? { ...booking, reviewed: true, rating: reviewData.rating }
            : booking
        )
      );
      
      // Close modal after 2 seconds
      setTimeout(() => {
        closeModal();
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting review:', err);
      setSubmitStatus('error');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to submit review. Please try again.',
        life: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get status severity for PrimeReact Tag
  const getStatusSeverity = (status) => {
    switch(status?.toLowerCase()) {
      case 'delivered': return 'success';
      case 'completed': return 'info';
      case 'pending': return 'warning';
      case 'processing': return 'secondary';
      default: return 'danger';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Dialog footer
  const dialogFooter = (
    <div className="flex flex-column sm:flex-row justify-content-end gap-2">
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        className="p-button-outlined w-full sm:w-auto" 
        onClick={closeModal}
      />
      <Button 
        label={isSubmitting ? 'Submitting...' : 'Submit Review'} 
        icon="pi pi-check" 
        className="p-button-primary w-full sm:w-auto" 
        onClick={handleSubmitReview}
        loading={isSubmitting}
      />
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center min-h-screen p-4">
        <div className="text-center">
          <ProgressSpinner />
          <p className="mt-3 text-muted">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-content-center align-items-center min-h-screen p-4">
        <div className="text-center w-full">
          <Message severity="error" text={error} className="mb-3 w-full" />
          <Button 
            label="Retry" 
            icon="pi pi-refresh" 
            className="p-button-primary" 
            onClick={fetchUserBookings}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      <Toast ref={toast} position="top-center" />
      
      <div className="grid p-2 p-sm-3 p-md-4">
        <div className="col-12">
          <Card className="shadow-none sm:shadow-2">
            {/* Header Section - Mobile Optimized */}
            <div className="text-center mb-3 mb-sm-4">
              <i className="pi pi-shopping-bag text-4xl sm:text-5xl text-primary mb-2"></i>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-800 m-0">
                My Orders
              </h2>
              <p className="text-600 text-sm sm:text-base mt-2">
                View your order history and leave reviews
              </p>
            </div>
            
            <Divider className="my-2 my-sm-3" />
            
            {bookings.length === 0 ? (
              <div className="text-center py-5">
                <i className="pi pi-inbox text-5xl sm:text-6xl text-400 mb-3"></i>
                <p className="text-600">No bookings found.</p>
              </div>
            ) : (
              <>
                {/* Mobile View - Accordion Layout */}
                <div className="block lg:hidden">
                  <Accordion className="booking-accordion">
                    {bookings.map((booking, index) => (
                      <AccordionTab 
                        key={booking.id}
                        header={
                          <div className="flex flex-column gap-2 w-full">
                            <div className="flex justify-content-between align-items-center">
                              <span className="font-bold text-primary text-base">
                                Order #{booking.id}
                              </span>
                              <Tag 
                                value={booking.status || 'Completed'} 
                                severity={getStatusSeverity(booking.status)}
                                rounded
                                size="small"
                              />
                            </div>
                            <div className="flex justify-content-between text-sm">
                              <span className="text-600">
                                {formatDate(booking.createdat || booking.date)}
                              </span>
                              <span className="font-bold text-primary">
                                ₹{booking.amount || booking.totalamount || 0}
                              </span>
                            </div>
                          </div>
                        }
                      >
                        <div className="p-2">
                          <div className="mb-3">
                            <div className="flex flex-column gap-2">
                              <div className="flex justify-content-between align-items-center py-1">
                                <span className="text-600 text-sm">Partner Shop:</span>
                                <span className="font-medium text-sm text-right">
                                  {booking.partnerName || `Shop #${booking.partnerShopId || booking.partnerid}`}
                                </span>
                              </div>
                              
                              <Divider className="my-1" />
                              
                              {booking.status === 'Delivered' && booking.reviewed && (
                                <>
                                  <div className="flex justify-content-between align-items-center py-1">
                                    <span className="text-600 text-sm">Your Rating:</span>
                                    <Rating value={booking.rating} readOnly cancel={false} />
                                  </div>
                                  <Divider className="my-1" />
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-3">
                            {booking.status === 'Delivered' ? (
                              booking.reviewed ? (
                                <Button 
                                  label="Review Submitted" 
                                  icon="pi pi-check" 
                                  className="p-button-outlined p-button-success w-full" 
                                  disabled 
                                  size="small"
                                />
                              ) : (
                                <Button 
                                  label="⭐ Write a Review" 
                                  className="p-button-primary w-full" 
                                  onClick={() => openReviewModal(booking)}
                                  size="small"
                                />
                              )
                            ) : (
                              <Button 
                                label="Review after delivery" 
                                icon="pi pi-lock" 
                                className="p-button-outlined p-button-secondary w-full" 
                                disabled 
                                size="small"
                              />
                            )}
                          </div>
                        </div>
                      </AccordionTab>
                    ))}
                  </Accordion>
                </div>

                {/* Tablet & Desktop View - Grid Cards */}
                <div className="hidden lg:block">
                  <div className="grid">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="col-12 md:col-6 xl:col-4">
                        <Card className="booking-card h-full">
                          <div className="flex justify-content-between align-items-start mb-3">
                            <div>
                              <span className="font-bold text-primary text-base block">
                                Order #{booking.id}
                              </span>
                              <span className="text-600 text-xs">
                                {formatDate(booking.createdat || booking.date)}
                              </span>
                            </div>
                            <Tag 
                              value={booking.status || 'Completed'} 
                              severity={getStatusSeverity(booking.status)}
                              rounded
                            />
                          </div>
                          
                          <Divider className="my-2" />
                          
                          <div className="mb-2">
                            <div className="flex justify-content-between align-items-center mb-2">
                              <span className="text-600 text-sm">Partner Shop:</span>
                              <span className="font-medium text-sm text-right">
                                {booking.partnerName || `Shop #${booking.partnerShopId || booking.partnerid}`}
                              </span>
                            </div>
                            <div className="flex justify-content-between align-items-center mb-2">
                              <span className="text-600 text-sm">Total Amount:</span>
                              <span className="font-bold text-primary">
                                ₹{booking.amount || booking.totalamount || 0}
                              </span>
                            </div>
                            
                            {booking.status === 'Delivered' && booking.reviewed && (
                              <div className="flex justify-content-between align-items-center mt-2 pt-2 border-top-1">
                                <span className="text-600 text-sm">Your Rating:</span>
                                <Rating value={booking.rating} readOnly cancel={false} />
                              </div>
                            )}
                          </div>
                          
                          <Divider className="my-3" />
                          
                          <div className="mt-3">
                            {booking.status === 'Delivered' ? (
                              booking.reviewed ? (
                                <Button 
                                  label="Review Submitted" 
                                  icon="pi pi-check" 
                                  className="p-button-outlined p-button-success w-full" 
                                  disabled 
                                />
                              ) : (
                                <Button 
                                  label="⭐ Write a Review" 
                                  className="p-button-primary w-full" 
                                  onClick={() => openReviewModal(booking)}
                                />
                              )
                            ) : (
                              <Button 
                                label="Review after delivery" 
                                icon="pi pi-lock" 
                                className="p-button-outlined p-button-secondary w-full" 
                                disabled 
                              />
                            )}
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary Section for Mobile */}
                <div className="block lg:hidden mt-3">
                  <Card className="bg-primary-50">
                    <div className="flex justify-content-between align-items-center">
                      <div>
                        <span className="text-sm text-600">Total Orders</span>
                        <h3 className="text-xl font-bold m-0">{bookings.length}</h3>
                      </div>
                      <div>
                        <span className="text-sm text-600">Completed</span>
                        <h3 className="text-xl font-bold m-0">
                          {bookings.filter(b => b.status === 'Delivered').length}
                        </h3>
                      </div>
                      <div>
                        <span className="text-sm text-600">Pending Review</span>
                        <h3 className="text-xl font-bold m-0">
                          {bookings.filter(b => b.status === 'Delivered' && !b.reviewed).length}
                        </h3>
                      </div>
                    </div>
                  </Card>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Review Dialog - Mobile Optimized */}
      <Dialog
        header="Write a Review"
        visible={showModal}
        style={{ width: '95vw', maxWidth: '500px' }}
        footer={dialogFooter}
        onHide={closeModal}
        draggable={false}
        resizable={false}
        position="top"
        className="mobile-dialog"
      >
        <div className="p-fluid">
          {/* Read-only fields */}
          <div className="field mb-3">
            <label className="font-medium text-600 text-sm">Order ID</label>
            <InputText 
              value={reviewData.bookingId} 
              disabled 
              className="p-component-sm w-full"
            />
          </div>
          
          <div className="field mb-3">
            <label className="font-medium text-600 text-sm">User ID</label>
            <InputText 
              value={reviewData.userId} 
              disabled 
              className="p-component-sm w-full"
            />
          </div>
          
          <div className="field mb-3">
            <label className="font-medium text-600 text-sm">Partner Shop ID</label>
            <InputText 
              value={reviewData.partnerShopId} 
              disabled 
              className="p-component-sm w-full"
            />
          </div>
          
          <Divider className="my-2" />
          
          {/* Rating */}
          <div className="field mb-3">
            <label className="font-medium text-600 text-sm block mb-2">Your Rating *</label>
            <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center gap-2">
              <Rating 
                value={reviewData.rating} 
                onChange={(e) => setReviewData(prev => ({ ...prev, rating: e.value }))}
                cancel={false}
                stars={5}
              />
              <span className="text-sm text-600">
                {reviewData.rating === 1 && 'Poor'}
                {reviewData.rating === 2 && 'Fair'}
                {reviewData.rating === 3 && 'Good'}
                {reviewData.rating === 4 && 'Very Good'}
                {reviewData.rating === 5 && 'Excellent'}
              </span>
            </div>
          </div>
          
          {/* Review Title */}
          <div className="field mb-3">
            <label htmlFor="reviewTitle" className="font-medium text-600 text-sm block mb-2">
              Review Title *
            </label>
            <InputText
              id="reviewTitle"
              value={reviewData.reviewTitle}
              onChange={(e) => handleInputChange(e, 'reviewTitle')}
              placeholder="Summarize your experience"
              maxLength={100}
              className="w-full"
            />
            <small className="text-500 text-xs">
              {reviewData.reviewTitle.length}/100 characters
            </small>
          </div>
          
          {/* Review Text */}
          <div className="field mb-3">
            <label htmlFor="reviewText" className="font-medium text-600 text-sm block mb-2">
              Your Review *
            </label>
            <InputTextarea
              id="reviewText"
              value={reviewData.reviewText}
              onChange={(e) => handleInputChange(e, 'reviewText')}
              placeholder="Share your experience with the laundry service..."
              rows={4}
              maxLength={500}
              className="w-full"
              autoResize
            />
            <small className="text-500 text-xs">
              {reviewData.reviewText.length}/500 characters
            </small>
          </div>
          
          {submitStatus === 'success' && (
            <Message severity="success" text="✓ Thank you! Your review has been submitted." className="mt-2 w-full" />
          )}
          
          {submitStatus === 'error' && (
            <Message severity="error" text="✗ Failed to submit review. Please try again." className="mt-2 w-full" />
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default BookingsWithReview;